import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { createSessionController } from '@vista-cloud/web-common';
import { api, setVistaToken } from '../lib/api.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    user: null,
    loading: true,
    error: null,
    // vista#671: this console had no error TYPE, only a message, so its gate
    // could not tell "not signed in" from "signed in but not an admin" — and
    // those need different affordances. A Sign In button helps the first and
    // is a dead end for the second.
    errorType: null,
  });

  // vista#671: the exchange → `/me` → refresh-timer plumbing and the 401/403
  // taxonomy live in `@vista-cloud/web-common`, hand-rolled here and in the org
  // dashboard until they drifted. Memoised so the controller — which owns a
  // timer — is created once rather than per render.
  const controller = useMemo(
    () =>
      createSessionController({
        api,
        setVistaToken,
        onUser: (user) => setState({ user, loading: false, error: null, errorType: null }),
        onError: ({ errorType, error }, rawError) => {
          // Mirrors the org console's #679 behaviour: a refresh that fails must
          // be diagnosable rather than looking like a silent logout.
          if (errorType === 'session_expired') {
            console.error('Session refresh failed:', rawError);
          }
          setState({ user: null, loading: false, error, errorType });
        },
        // No org_slug: this console is cross-org by definition.
      }),
    [],
  );

  const initSession = useCallback(() => controller.init(), [controller]);

  useEffect(() => {
    initSession();
    return () => controller.dispose();
  }, [initSession, controller]);

  const value = useMemo(
    () => ({
      user: state.user,
      loading: state.loading,
      error: state.error,
      errorType: state.errorType,
      isAuthenticated: !!state.user,
      refetch: initSession,
    }),
    [state, initSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
